import { useMutation } from '@provision/graphql-client';
import { useCallback, useRef } from 'react';
import {
  CHECK_SMS_DELIVERABILITY_MUTATION,
  type CheckSmsDeliverabilityMutationResult,
  type CheckSmsDeliverabilityMutationVars,
} from 'src/pages/Conversations/Chat/SmsSession/sms-session.operations';

export type SmsDeliverabilityTarget =
  | { conversationChannelId: string }
  | { clientPhoneNumberId: string };
export type SmsDeliverabilityVerdict =
  CheckSmsDeliverabilityMutationResult['checkSmsDeliverability'];

/** lazy Twilio Lookup, once per key per conversation; a failed one is retried on revisit */
export function useSmsDeliverabilityCheck(
  conversationId: string,
): (
  key: string,
  target: SmsDeliverabilityTarget,
) => Promise<SmsDeliverabilityVerdict | null> | null {
  const [checkDeliverability] = useMutation<
    CheckSmsDeliverabilityMutationResult,
    CheckSmsDeliverabilityMutationVars
  >(
    CHECK_SMS_DELIVERABILITY_MUTATION,
    // with onError set, Apollo resolves the un-awaited call instead of rejecting it
    { onError: (error) => console.warn('checkSmsDeliverability failed', error) },
  );
  const asked = useRef<Set<string>>(new Set());
  const prevConversationIdRef = useRef(conversationId);
  if (prevConversationIdRef.current !== conversationId) {
    prevConversationIdRef.current = conversationId;
    asked.current.clear();
  }

  return useCallback(
    (key, target) => {
      if (asked.current.has(key)) return null;
      asked.current.add(key);
      return checkDeliverability({ variables: { input: target } }).then(
        (result) => result.data?.checkSmsDeliverability ?? null,
      );
    },
    [checkDeliverability],
  );
}
